/* eslint-disable prettier/prettier */
import { v4 as uuidv4 } from 'uuid';
import { cloneDeep } from 'lodash';

export const divide = (dividend, divisor) => {
    if (!divisor || divisor === 0 || divisor === '0' || divisor === undefined) return 0.0;
    const quotient = dividend / divisor;
    return Math.round(quotient * 100) / 100;
};

const roundFloat = (value) => parseFloat(value.toFixed(2));

const calculateGroupGrossAdditionalSpend = ({ additionalSpend, groupWash, groupWashPercent }) => {
    let totalGrossSpend = 0;
    additionalSpend.forEach((spend) => {
        const grossAmount = parseInt(spend.value, 10);
        const grossSpend =
            groupWash && spend.applyWash ? grossAmount - grossAmount * divide(groupWashPercent, 100) : grossAmount;
        totalGrossSpend += grossSpend;
    });
    return totalGrossSpend;
};

const calculateSummary = (groupDisplacement) => {
    let forecastRooms = 0;
    let forecastRoomsGroup = 0;
    let displacedRooms = 0;
    let totalNetADR = 0;
    let totalGrossADR = 0;
    let totalRooms = 0;
    groupDisplacement.forecasts.forEach((forecast) => {
        forecastRooms += parseInt(forecast.forecast, 10);
        forecastRoomsGroup += parseInt(forecast.groupSize, 10) - forecast.washRooms;
        displacedRooms += parseInt(forecast.displacedRooms, 10);
        const roomType = groupDisplacement.roomTypes.find((room) => room.roomType === forecast.name);
        if (roomType) {
            totalNetADR += parseInt(roomType.netADR, 10);
            totalGrossADR += parseInt(roomType.estADR, 10);
        }
    });
    const averageNetADR = parseFloat(divide(totalNetADR, groupDisplacement.forecasts.length));
    const averageGrossADR = parseFloat(divide(totalGrossADR, groupDisplacement.forecasts.length));
    const revenueNet = forecastRooms * averageNetADR;

    let occupiedRoomCost = 0;
    groupDisplacement.roomTypes.forEach((roomType) => {
        totalRooms += roomType.hotelSize;
        occupiedRoomCost += roomType.occupiedRoomCost;
    });
    occupiedRoomCost /= groupDisplacement.roomTypes.length;
    totalRooms *= groupDisplacement.forecasts.length;
    const totalAdditionalSpendWithoutGroup = groupDisplacement.averageNormalSpend.net * forecastRooms;
    const totalAdditionalSpendGroup =
        groupDisplacement.totalAdditionalSpend +
        (forecastRooms - displacedRooms) * groupDisplacement.averageNormalSpend.net;
    const revenueGroupStayingNonGroup = (forecastRooms - displacedRooms) * averageNetADR;

    let revenueGroupStayingGroup = groupDisplacement.groupBudget * forecastRoomsGroup;
    if (groupDisplacement.groupCommission) {
        revenueGroupStayingGroup -=
            divide(parseInt(groupDisplacement.groupCommissionPercent, 10), 100) * revenueGroupStayingGroup;
    }

    revenueGroupStayingGroup -= occupiedRoomCost * forecastRoomsGroup;

    let revenueGroup = revenueGroupStayingNonGroup + revenueGroupStayingGroup;
    revenueGroup = revenueGroup < 0 ? 0 : revenueGroup;

    // gross calculations - accommodation + spend (minus wash for group)
    const totalAdditionalSpendWithoutGroupGross = groupDisplacement.averageNormalSpend.gross * forecastRooms;
    const revenueGross = forecastRooms * averageGrossADR + totalAdditionalSpendWithoutGroupGross;
    let revenueGrossGroup =
        (forecastRooms - displacedRooms) * averageGrossADR + groupDisplacement.groupBudget * forecastRoomsGroup;
    revenueGrossGroup = revenueGrossGroup < 0 ? 0 : revenueGrossGroup;
    revenueGrossGroup += calculateGroupGrossAdditionalSpend(groupDisplacement);
    revenueGrossGroup += (forecastRooms - displacedRooms) * groupDisplacement.averageNormalSpend.gross;

    const withoutGroup = {
        id: 'withoutGroup',
        rowHeader: 'Without Group',
        rooms: forecastRooms,
        revenue: revenueNet.toFixed(2),
        gross: revenueGross.toFixed(2),
        adr: averageNetADR.toFixed(2),
        revPar: divide(revenueNet, totalRooms).toFixed(2),
        additionalSpend: totalAdditionalSpendWithoutGroup.toFixed(2),
        trevPar: divide(revenueNet + totalAdditionalSpendWithoutGroup, totalRooms).toFixed(2),
    };

    const withGroup = {
        id: 'withGroup',
        rowHeader: groupDisplacement.groupWash ? 'With Group (Wash applied)' : 'With Group',
        rooms: forecastRooms - displacedRooms + forecastRoomsGroup,
        revenue: revenueGroup.toFixed(2),
        gross: revenueGrossGroup.toFixed(2),
        adr: divide(revenueGroup, forecastRooms - displacedRooms + forecastRoomsGroup).toFixed(2),
        revPar: divide(revenueGroup, totalRooms).toFixed(2),
        additionalSpend: totalAdditionalSpendGroup.toFixed(2),
        trevPar: divide(revenueGroup + totalAdditionalSpendGroup, totalRooms).toFixed(2),
    };

    return [withoutGroup, withGroup];
};

export const calculateBreakEven = (
    room,
    forecasts,
    averageNormalSpend,
    groupSpendPerRoomPerNight,
    groupCommission,
    groupCommissionPercent,
) => {
    let revenueNoGroup = 0;
    let spendNoGroup = 0;
    let revenueNonGroupWithGroupStaying = 0;
    let spendNonGroupWithGroupStaying = 0;
    let spendGroup = 0;
    let groupNights = 0;
    let compRooms = 0;
    forecasts.forEach((forecast) => {
        revenueNoGroup += forecast.forecast * room.netADR;
        spendNoGroup += forecast.forecast * averageNormalSpend.net;
        revenueNonGroupWithGroupStaying += (forecast.forecast - forecast.displacedRooms) * room.netADR;
        spendNonGroupWithGroupStaying += (forecast.forecast - forecast.displacedRooms) * averageNormalSpend.net;
        spendGroup += forecast.groupSize * groupSpendPerRoomPerNight;
        groupNights += parseInt(forecast.groupSize, 10) - forecast.washRooms;
        compRooms += parseInt(forecast.compRooms, 10);
    });
    const totalBreakEvenAmount = revenueNoGroup + spendNoGroup;
    const totalGroupBreakEvenAmount =
        totalBreakEvenAmount - revenueNonGroupWithGroupStaying - spendNonGroupWithGroupStaying;

    let acommodationGroupBreakEven = totalGroupBreakEvenAmount - spendGroup;
    acommodationGroupBreakEven += groupNights * room.occupiedRoomCost;

    if (groupCommission) {
        const percentWithCommissionApplied = 100 - groupCommissionPercent;
        acommodationGroupBreakEven = divide(acommodationGroupBreakEven, percentWithCommissionApplied) * 100;
    }

    return divide(acommodationGroupBreakEven, groupNights - compRooms);
};

export const calculateMinRate = (state) => {
    const updatedState = cloneDeep(state);
    let totalOutcome = 0;
    updatedState.groupDisplacement.roomTypes.forEach((room) => {
        const { occupiedRoomCost, estADR } = room;
        const { totalAdditionalSpend, averageNormalSpend } = updatedState.groupDisplacement;
        const forecasts = updatedState.groupDisplacement.forecasts.filter(
            (forecast) => forecast.name === room.roomType,
        );
        let totalGroupSize = 0;
        let totalWash = 0;
        let totalDisplaced = 0;
        forecasts.forEach((forecast) => {
            totalGroupSize += parseFloat(forecast.groupSize);
            totalWash += parseFloat(forecast.expectedGroupWash);
            totalDisplaced += parseFloat(forecast.displacedRooms);
        });
        const averageGroupSize = divide(totalGroupSize, forecasts.length);
        const averageWash = divide(totalWash, forecasts.length);
        const averageDisplaced = divide(totalDisplaced, forecasts.length);

        const adrNormalSpendWithoutOcc = parseFloat(estADR) + averageNormalSpend.net - parseFloat(occupiedRoomCost);

        const additionalSpendAcrossGroupIncWash = divide(
            parseFloat(totalAdditionalSpend),
            parseFloat(averageGroupSize) - parseFloat(averageWash),
        );

        const resultWithoutWash = calculateBreakEven(
            room,
            forecasts,
            averageNormalSpend,
            updatedState.groupDisplacement.totalAdditionalSpendPerRoomPerNight,
            updatedState.groupDisplacement.groupCommission,
            updatedState.groupDisplacement.groupCommissionPercent,
        );
        const resultWithWash = divide(
            parseFloat(occupiedRoomCost) -
            additionalSpendAcrossGroupIncWash +
            adrNormalSpendWithoutOcc * averageDisplaced,
            parseFloat(averageGroupSize) - parseFloat(averageWash),
        );

        /* eslint-disable no-param-reassign */
        room.minRate = resultWithoutWash.toFixed(2);
        room.minRateIncWash = resultWithWash.toFixed(2);
        /* eslint-enable no-param-reassign */
        totalOutcome += parseFloat(resultWithoutWash.toFixed(2));
    });
    const { groupBudget } = updatedState.groupDisplacement;
    const amountToRemoveFromGroupBudget = divide(totalOutcome, updatedState.groupDisplacement.roomTypes.length);

    const outcomeAmount = parseFloat(groupBudget) - amountToRemoveFromGroupBudget;
    updatedState.groupDisplacement.outcome =
        outcomeAmount > 0 ? `gain ${outcomeAmount.toFixed(2)}` : `lose ${outcomeAmount.toFixed(2)}`;
    updatedState.groupDisplacement.action = outcomeAmount > 0 ? `accept` : `reject`;

    updatedState.groupDisplacement.summary = calculateSummary(state.groupDisplacement);
    return updatedState;
};

export const calculateInclusionAmount = (state, pricePer, value) => {
    let { numberOfAdults, numberOfChildren, numberOfInfants, numberOfNights } = state.packageCalculator;
    numberOfAdults = parseFloat(numberOfAdults);
    numberOfChildren = parseFloat(numberOfChildren);
    numberOfInfants = parseFloat(numberOfInfants);
    numberOfNights = parseFloat(numberOfNights);
    switch (pricePer) {
        case 'perItem':
            return parseFloat(value);
        case 'perPersonExcInfant':
            return (numberOfAdults + numberOfChildren) * value;
        case 'perPersonIncInfant':
            return (numberOfAdults + numberOfChildren + numberOfInfants) * value;
        case 'perAdult':
            return numberOfAdults * value;
        case 'perChildExcInfant':
            return numberOfChildren * value;
        case 'perChildIncInfant':
            return (numberOfChildren + numberOfInfants) * value;
        case 'perInfant':
            return numberOfInfants * value;
        case 'perNight':
            return numberOfNights * value;
        case 'perAdultPerNight':
            return numberOfNights * numberOfAdults * value;
        case 'perChildExcInfantPerNight':
            return numberOfNights * numberOfChildren * value;
        case 'perChildIncInfantPerNight':
            return numberOfNights * (numberOfChildren + numberOfInfants) * value;
        case 'perInfantPerNight':
            return numberOfNights * numberOfInfants * value;
        case 'perPersonExcInfantPerNight':
            return numberOfNights * (numberOfAdults + numberOfChildren) * value;
        case 'perPersonIncInfantPerNight':
            return numberOfNights * (numberOfAdults + numberOfChildren + numberOfInfants) * value;
        default:
            return 0;
    }
};

const calculateProfit = (updatedState) => {
    /* eslint-disable no-param-reassign */
    updatedState.packageCalculator.vatAmount =
        (updatedState.packageCalculator.vat / (100 + parseFloat(updatedState.packageCalculator.vat))) *
        updatedState.packageCalculator.proposedRate;
    updatedState.packageCalculator.commissionAmount =
        (updatedState.packageCalculator.commissionPercentage / 100) * updatedState.packageCalculator.proposedRate;
    updatedState.packageCalculator.totalReductions =
        updatedState.packageCalculator.vatAmount + updatedState.packageCalculator.commissionAmount;
    updatedState.packageCalculator.inclusions.forEach((item) => {
        const element = item;
        if (item.applied) {
            element.amount = calculateInclusionAmount(updatedState, item.pricePer, item.value).toFixed(2);
        } else {
            element.amount = 0;
        }
        updatedState.packageCalculator.totalReductions += parseFloat(item.amount);
        updatedState.packageCalculator.totalCostOfOccupiedRoom =
            parseFloat(updatedState.packageCalculator.costOfOccupiedRoom) *
            parseFloat(updatedState.packageCalculator.numberOfNights);
    });
    updatedState.packageCalculator.netProfit =
        parseFloat(updatedState.packageCalculator.proposedRate) -
        parseFloat(updatedState.packageCalculator.totalReductions) -
        parseFloat(updatedState.packageCalculator.costOfOccupiedRoom) *
        parseFloat(updatedState.packageCalculator.numberOfNights);
    /* eslint-enable no-param-reassign */
    return updatedState;
};

export const updatePackageCalculatorInclusions = (state, fieldId, actionType, value) => {
    const updatedState = cloneDeep(state);

    if (actionType === 'CREATE') {
        updatedState.packageCalculator.inclusions.push({
            id: fieldId,
            name: '',
            value: '0',
            pricePer: '',
            amount: 0,
            applied: true,
            isPreset: false,
        });
    } else if (actionType === 'DELETE') {
        const indexOfFieldToDelete = updatedState.packageCalculator.inclusions.findIndex((item) => item.id === fieldId);
        updatedState.packageCalculator.inclusions = [
            ...updatedState.packageCalculator.inclusions.slice(0, indexOfFieldToDelete),
            ...updatedState.packageCalculator.inclusions.slice(indexOfFieldToDelete + 1),
        ];
    } else {
        const fieldToUpdate = updatedState.packageCalculator.inclusions.find((item) => item.id === fieldId);

        if (fieldToUpdate !== undefined) {
            if (actionType === 'NAME') fieldToUpdate.name = value;
            if (actionType === 'VALUE') fieldToUpdate.value = value;
            if (actionType === 'PRICEPER') fieldToUpdate.pricePer = value;
            if (actionType === 'TOGGLE') fieldToUpdate.applied = value;
        }
    }

    return calculateProfit(updatedState);
};

export const updatePackageCalculatorIncrements = (state, fieldName, value) => {
    const updatedState = cloneDeep(state);
    updatedState.packageCalculator[fieldName] = value;
    // Increments are calculated by the component itself, not stored in the store, so nothing else needs to happen
    // But calculations could be done and stored here
    return updatedState;
};

export const updatePackageCalculatorProposal = (state, fieldName, value) => {
    const updatedState = cloneDeep(state);
    updatedState.packageCalculator[fieldName] = value;
    return calculateProfit(updatedState);
};

const calculateRoomBreakdown = (state) => {
    /* eslint-disable no-param-reassign  */
    const numberOfRooms = parseFloat(state.occupiedCostCalculator.roomBreakdown.numberOfRooms) || 0;
    const daysOpenPerPeriod = parseFloat(state.occupiedCostCalculator.roomBreakdown.daysOpenPerPeriod) || 0;
    const averageOccupancy = parseFloat(state.occupiedCostCalculator.roomBreakdown.averageOccupancy) || 0;

    const totalRoomCountPerPeriod = numberOfRooms * daysOpenPerPeriod;
    state.occupiedCostCalculator.roomBreakdown.totalRoomCountPerPeriod = totalRoomCountPerPeriod;
    state.occupiedCostCalculator.roomBreakdown.averageOccupiedRoomCountPerPeriod =
        totalRoomCountPerPeriod * (averageOccupancy / 100);
    /* eslint-enable no-param-reassign  */
    return state;
};

export const generateRooms = (state, value) => {
    const updatedState = cloneDeep(state);
    updatedState.groupDisplacement.roomTypes = [];
    if (value > 0) {
        for (let index = 0; index < value; index += 1) {
            updatedState.groupDisplacement.roomTypes.push({
                roomType: 'Room Type',
                hotelSize: 0,
                estADR: 0,
                occupiedRoomCost: 0,
                netADR: 0,
                minRateIncWash: 0,
                minRate: 0,
                id: uuidv4(),
            });
        }
    } else {
        updatedState.groupDisplacement.roomTypes.push({
            roomType: 'Whole House',
            hotelSize: 0,
            estADR: 0,
            occupiedRoomCost: 0,
            netADR: 0,
            minRateIncWash: 0,
            minRate: 0,
            id: uuidv4(),
        });
    }

    return updatedState;
};

export const updateADRData = (state, data) => {
    const updatedState = cloneDeep(state);
    const newData = data;
    const existingIndex = updatedState.groupDisplacement.roomTypes.findIndex((roomType) => roomType.id === newData.id);
    updatedState.groupDisplacement.roomTypes[existingIndex] = newData;

    updatedState.groupDisplacement.roomTypes.forEach((item) => {
        const roomType = item;
        roomType.netADR = parseFloat(roomType.estADR) - parseFloat(roomType.occupiedRoomCost);
    });
    return updatedState;
};

export const updateForecastData = (state, data, roomType) => {
    const updatedState = cloneDeep(state);
    let existingIndex = -1;
    existingIndex = updatedState.groupDisplacement.forecasts.findIndex(
        (item) => item.name === roomType && item.date === data.date,
    );
    if (existingIndex >= 0) {
        updatedState.groupDisplacement.forecasts[existingIndex] = {
            name: roomType,
            ...data,
        };
    } else {
        updatedState.groupDisplacement.forecasts.push({
            name: roomType,
            ...data,
        });
    }

    updatedState.groupDisplacement.forecasts.forEach((item) => {
        const forecast = item;
        const forecastRoomType = updatedState.groupDisplacement.roomTypes.find((room) => room.roomType === item.name);
        const availableRooms = parseFloat(forecastRoomType.hotelSize) - parseFloat(forecast.forecast);
        let displacedRooms = parseFloat(forecast.groupSize) - availableRooms;
        forecast.washRooms = 0;
        if (updatedState.groupDisplacement.groupWash) {
            const washRooms = Math.ceil(
                forecast.groupSize * divide(updatedState.groupDisplacement.groupWashPercent, 100),
            );
            forecast.washRooms = washRooms;
            displacedRooms -= washRooms;
        }
        displacedRooms = displacedRooms < 0 ? 0 : displacedRooms;
        forecast.displacedRooms = displacedRooms;
    });
    return updatedState;
};

export const updateAllForecasts = (state) => {
    const updatedState = cloneDeep(state);
    updatedState.groupDisplacement.forecasts.forEach((item) => {
        const forecast = item;
        const forecastRoomType = updatedState.groupDisplacement.roomTypes.find((room) => room.roomType === item.name);
        const availableRooms = parseFloat(forecastRoomType.hotelSize) - parseFloat(forecast.forecast);
        let displacedRooms = parseFloat(forecast.groupSize) - availableRooms;
        forecast.washRooms = 0;
        if (updatedState.groupDisplacement.groupWash) {
            const washRooms = Math.ceil(
                forecast.groupSize * divide(updatedState.groupDisplacement.groupWashPercent, 100),
            );
            forecast.washRooms = washRooms;
            displacedRooms -= washRooms;
        }
        displacedRooms = displacedRooms < 0 ? 0 : displacedRooms;
        forecast.displacedRooms = displacedRooms;
    });
    return updatedState;
};

/**
 * Calculate all calculated fields in the Cost of Occupied Room form
 * Yes it updates the parameter and returns it, which is pointless. It's fine
 * @param {Object} updatedState - a COPY of the state, that can be mutated (and will be)
 * @param {String} formSection - section that changed and needs re-calculated
 * @return {Object} the passed parameter, updated
 */
export const calculateCORForSection = (updatedState, formSection) => {
    /* eslint-disable no-param-reassign  */
    const averageDaysOpen =
        parseFloat(updatedState.occupiedCostCalculator.roomBreakdown.averageOccupiedRoomCountPerPeriod) || 0;

    updatedState.occupiedCostCalculator.cpor[formSection] = 0;
    updatedState.occupiedCostCalculator[formSection].forEach((item) => {
        if (updatedState.occupiedCostCalculator.sections[formSection] && item.applied) {
            const { percentage } = item;
            const periodTotal = parseFloat(item.value) || 0;

            item.amount = parseFloat(((percentage / 100) * periodTotal).toFixed(2));
            item.cpor = averageDaysOpen === 0 ? 0 : item.amount / averageDaysOpen;
        } else {
            item.amount = 0;
            item.cpor = 0;
        }
        updatedState.occupiedCostCalculator.cpor[formSection] += item.cpor;
    });
    /* eslint-enable no-param-reassign  */
    return updatedState;
};

/**
 * Re-calculate all calculated fields in the Cost of Occupied Room form, for ALL sections
 * THis is required when something shared by all sections is modified, e.g. roomBreakdown
 * @param {Object} updatedState - a COPY of the state, that can be mutated (and will be)
 * @return {Object} the passed parameter, updated
 */
export const calculateCOR = (updatedState) => {
    /* eslint-disable no-param-reassign  */
    updatedState = calculateRoomBreakdown(updatedState);
    ['roomConsumables', 'fixedCosts', 'salaryCosts', 'commission', 'variables'].forEach((section) => {
        updatedState = calculateCORForSection(updatedState, section);
    });
    /* eslint-enable no-param-reassign  */
    return updatedState;
};

export const updateCORFormItem = (state, sectionName, fieldId, actionType, value) => {
    const updatedState = cloneDeep(state);

    if (actionType === 'CREATE') {
        updatedState.occupiedCostCalculator[sectionName].push({
            id: fieldId,
            name: '',
            value: '0',
            percentage: 100, // unused
            cpor: 0,
            applied: true, // always true
        });
    } else if (actionType === 'DELETE') {
        const indexOfFieldToDelete = updatedState.occupiedCostCalculator[sectionName].findIndex(
            (item) => item.id === fieldId,
        );
        updatedState.occupiedCostCalculator[sectionName] = [
            ...updatedState.occupiedCostCalculator[sectionName].slice(0, indexOfFieldToDelete),
            ...updatedState.occupiedCostCalculator[sectionName].slice(indexOfFieldToDelete + 1),
        ];
    } else {
        const fieldToUpdate = updatedState.occupiedCostCalculator[sectionName].find((item) => item.id === fieldId);

        if (fieldToUpdate !== undefined) {
            if (actionType === 'NAME') fieldToUpdate.name = value;
            if (actionType === 'VALUE') fieldToUpdate.value = value;
            if (actionType === 'PERCENTAGE') fieldToUpdate.percentage = value;
            if (actionType === 'TOGGLE') fieldToUpdate.applied = value;
        }
    }

    return calculateCORForSection(updatedState, sectionName);
};

export const updateCORRoomBreakdown = (state, fieldName, value) => {
    const updatedState = cloneDeep(state);
    updatedState.occupiedCostCalculator.roomBreakdown[fieldName] = value;

    return calculateCOR(updatedState);
};

export const updateCORSectionToggle = (state, sectionName, value) => {
    const updatedState = cloneDeep(state);

    updatedState.occupiedCostCalculator.sections[sectionName] = value;

    return calculateCORForSection(updatedState, sectionName);
};

const calculateTotalAdditionalSpendPerRoomPerNight = (groupDisplacement, totalAdditionalSpend) => {
    let totalGroupSize = 0;
    groupDisplacement.forecasts.forEach((forecast) => {
        totalGroupSize += parseFloat(forecast.groupSize);
    });

    return divide(totalAdditionalSpend, totalGroupSize);
};

const applyCalculationsToAllSpendItems = (state) => {
    const updatedState = cloneDeep(state);
    const { groupDisplacement } = updatedState;

    groupDisplacement.totalAdditionalSpend = 0;
    groupDisplacement.totalAdditionalSpendPerRoomPerNight = 0;
    (groupDisplacement.additionalSpend || []).forEach((item) => {
        const { percentage } = item;
        const grossValue = parseFloat(item.value) || 0;

        let amount = 0;
        if (item.applied) {
            amount = roundFloat((percentage / 100) * grossValue);
            if (groupDisplacement.groupWash && item.applyWash) {
                const groupWashPercent = parseFloat(groupDisplacement.groupWashPercent) || 0;
                amount -= roundFloat(amount * divide(groupWashPercent, 100));
            }
            if (groupDisplacement.groupCommission && item.applyCommission) {
                const groupCommissionPercent = parseFloat(groupDisplacement.groupCommissionPercent) || 0;
                amount -= roundFloat(amount * divide(groupCommissionPercent, 100));
            }
        }
        item.amount = amount; // eslint-disable-line no-param-reassign
        groupDisplacement.totalAdditionalSpend += item.amount;
    });
    groupDisplacement.totalAdditionalSpendPerRoomPerNight = calculateTotalAdditionalSpendPerRoomPerNight(
        updatedState.groupDisplacement,
        updatedState.groupDisplacement.totalAdditionalSpend,
    );
    updatedState.groupDisplacement = groupDisplacement;
    return updatedState;
};

export const toggleAdditionalSpend = (state, enabled) => {
    let updatedState = cloneDeep(state);
    if (enabled) {
        updatedState = applyCalculationsToAllSpendItems(state);
    } else {
        updatedState.groupDisplacement.additionalSpend.forEach((item) => {
            const element = item;
            element.amount = 0;
        });
        updatedState.groupDisplacement.totalAdditionalSpend = 0;
        updatedState.groupDisplacement.totalAdditionalSpendPerRoomPerNight = 0;
    }
    return updatedState;
};

export const calculateAdditionalSpend = (state, fieldId, actionType, value) => {
    const updatedState = cloneDeep(state);

    if (actionType === 'CREATE') {
        updatedState.groupDisplacement.additionalSpend.push({
            id: fieldId,
            name: '',
            value: '0',
            amount: 0,
            percentage: 100,
            applied: true,
            applyWash: false,
            applyCommission: false,
        });
    } else if (actionType === 'DELETE') {
        const indexOfFieldToDelete = updatedState.groupDisplacement.additionalSpend.findIndex(
            (item) => item.id === fieldId,
        );
        updatedState.groupDisplacement.additionalSpend = [
            ...updatedState.groupDisplacement.additionalSpend.slice(0, indexOfFieldToDelete),
            ...updatedState.groupDisplacement.additionalSpend.slice(indexOfFieldToDelete + 1),
        ];
    } else {
        const fieldToUpdate = updatedState.groupDisplacement.additionalSpend.find((item) => item.id === fieldId);

        if (fieldToUpdate !== undefined) {
            if (actionType === 'NAME') fieldToUpdate.name = value;
            if (actionType === 'VALUE') fieldToUpdate.value = value;
            if (actionType === 'PERCENTAGE') fieldToUpdate.percentage = value;
            if (actionType === 'TOGGLE') fieldToUpdate.applied = value;
            if (actionType === 'TOGGLEWASH') fieldToUpdate.applyWash = value;
            if (actionType === 'TOGGLECOMMISSION') fieldToUpdate.applyCommission = value;
        }
    }

    return applyCalculationsToAllSpendItems(updatedState);
};

export const calculateNormalSpend = (state, action, value) => {
    const updatedState = cloneDeep(state);

    const { averageNormalSpend } = updatedState.groupDisplacement;

    if (action === 'VALUE') {
        averageNormalSpend.gross = value;
    }
    if (action === 'PERCENTAGE') {
        averageNormalSpend.percent = value;
    }

    const gross = parseFloat(averageNormalSpend.gross) || 0;
    averageNormalSpend.net = roundFloat((averageNormalSpend.percent / 100) * gross);

    return updatedState;
};
